import React, { useEffect, useState, ChangeEvent } from "react";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { InfoOutlined } from "@material-ui/icons";
import { makeStyles, createStyles } from "@material-ui/styles";
import {
  Theme,
  Grid,
  Typography,
  InputAdornment,
  Card,
  CardContent,
} from "@material-ui/core";
import * as Yup from "yup";
import clsx from "clsx";
import useSnackbar from "../../../hooks/snackbar/useSnackbar";
import useChangeTracker from "../../../hooks/change-tracker/useChangeTracker";
import ValidationConfiguration from "../../../hooks/change-tracker/ValidationConfiguration";
import TrackerName from "../../../models/payment-request/PaymentRequestTrackerName";
import useInput from "../../../hooks/input/useInput";
import TrTextField from "../../shared/controls/TrTextField";
import TrSnackbar from "../../shared/controls/TrSnackbar";
import StepHandler from "../../../models/shared/StepHandler";
import PaymentRequestSchoolCarrier from "../../../models/payment-request/PaymentRequestSchoolCarrier";
import PaymentRequestSchoolCarrierData from "../../../models/payment-request/PaymentRequestSchoolCarrierData";
import PaymentRequestApiClient from "../../../api/PaymentRequestApiClient";
import {
  nameof,
  getTooltipForDecimalInput,
  getCurrencyDisplayName,
} from "../../../utilities/utilities";
import { numberFormat, currencyFormat } from "../../../utilities/utilities";
import Currency from "../../../models/shared/Currency";

interface Props extends StepHandler {
  schoolCarriers: PaymentRequestSchoolCarrier[];
  schoolCarriersDataState: PaymentRequestSchoolCarrierData[];
  schoolYearId: number;
  currency: Currency;
  updateSchoolCarriersData: (
    schoolCarriersData: PaymentRequestSchoolCarrierData[]
  ) => void;
}

export default function PaymentRequestSchoolCarrierDataStep({
  schoolCarriers,
  schoolCarriersDataState,
  schoolYearId,
  currency,
  updateSchoolCarriersData,
  buttons,
  onPrevious,
  onNext,
  goToPreviousStep,
  goToNextStep,
  onStepperSave,
}: Props) {
  const classes = useStyles({});

  const [schoolCarriersData, setSchoolCarriersData] = useState<
    PaymentRequestSchoolCarrierData[]
  >([]);

  useEffect(() => {
    if (schoolCarriersDataState.length === 0) {
      let newSchoolCarriersData: PaymentRequestSchoolCarrierData[] = [];
      schoolCarriers.forEach((sc) => {
        if (sc.isSelected) {
          newSchoolCarriersData.push({
            schoolCarrier: sc,
          });
        }
      });

      setSchoolCarriersData(newSchoolCarriersData);
    } else {
      setSchoolCarriersData(schoolCarriersDataState);
    }
  }, [schoolCarriersDataState, schoolCarriers]);

  useEffect(() => {
    return updateTrackerDebounceEffect([
      [TrackerName.SchoolCarrierData, schoolCarriersData],
    ]);
  }, [schoolCarriersData]);

  const { handleNumberChangeArray, handleTextChangeArray } = useInput();
  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    message: snackbarMessage,
    close: closeSnackbar,
    variant: snackbarVariant,
  } = useSnackbar();

  const validationConfig = {
    trackerName: TrackerName.SchoolCarrierData,
    validationScheme: Yup.array().of(
      Yup.object<PaymentRequestSchoolCarrierData[]>()
        .shape({
          numberOfDays: Yup.number()
            .nullable()
            .min(0, "Broj dana mora biti između 0-24.")
            .max(24, "Broj dana mora biti između 0-24.")
            .required(),
        })
        .test("SchoolCarrierData", "Greška na prijevozu.", function (
          value: PaymentRequestSchoolCarrierData
        ) {
          const { path, createError, parent } = this;

          if (
            value.transportCost &&
            value.transportCostCorrected != null &&
            value.transportCost <= value.transportCostCorrected
          ) {
            return createError({
              path: path,
              message:
                "Korigirana cijena treba biti manja od početno izračunate cijene.",
            });
          }

          if (
            value.transportCostCorrected != null &&
            !isNaN(value.transportCostCorrected) &&
            (value.remark == null || value.remark == "" || value.remark == " ")
          ) {
            return createError({
              path: path,
              message:
                "Uz korigiranu cijenu potrebno je unijeti i napomenu kako biste nastavili na sljedeći korak zahtjeva.",
            });
          }

          return true;
        })
    ),
  } as ValidationConfiguration;

  const {
    validationResult,
    updateTrackerDebounceEffect,
    showErrors,
  } = useChangeTracker([validationConfig], false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, value, type } = e.target;
    if (type === "number")
      handleNumberChangeArray(e, index, setSchoolCarriersData);
    else handleTextChangeArray(e, index, setSchoolCarriersData);
    e.target.focus();
  };

  const handleCostCorrectedBlur = (e: any, index: number) => {
    const { value, type } = e.target;
    const updatedCarriers = schoolCarriersData.map((carrier, i) => {
      if (i === index) {
        if (
          value == null ||
          value == "" ||
          (type === "number" && isNaN(+value))
        ) {
          return {
            ...carrier,
            remark: undefined,
          };
        }
      }

      return carrier;
    });

    setSchoolCarriersData(updatedCarriers);
  };

  const handleCalculateCost = async (index: number) => {
    const schoolCarrier = schoolCarriersData[index].schoolCarrier;
    const numberOfDays = schoolCarriersData[index].numberOfDays;

    const apiClient = new PaymentRequestApiClient();
    const schoolCarrierCost = await apiClient.calculateSchoolCarrierCost(
      schoolYearId,
      schoolCarrier.id,
      numberOfDays || 0
    );

    setSchoolCarriersData((previousState) =>
      previousState.map((ps) => {
        if (ps.schoolCarrier.id === schoolCarrier.id) {
          return {
            ...ps,
            transportCost: schoolCarrierCost.transportCost,
          };
        } else {
          return ps;
        }
      })
    );
  };

  const handleNextStep = () => {
    if (!validationResult.isValid(false)) {
      showErrors();

      const validationMessages = validationResult
        .getTrackerMessages(TrackerName.SchoolCarrierData, false)
        .filter(
          (m) =>
            m.validationType === "min" ||
            m.validationType === "max" ||
            m.validationType === "SchoolCarrierData"
        );

      let message = "";
      if (validationMessages.length > 0) {
        message = validationMessages[0].message;
      } else {
        message =
          "Ne možete prijeći na sljedeći korak dok ne ispunite sva obavezna polja. Molimo provjerite vrijednosti u crveno označenim poljima.";
      }

      openSnackbar(message, "error");
    } else {
      updateSchoolCarriersData(schoolCarriersData);
      goToNextStep && goToNextStep();
    }
  };

  const handlePreviousStep = () => {
    if (goToPreviousStep != null) {
      updateSchoolCarriersData(schoolCarriersData);
      goToPreviousStep();
    }
  };

  const handleUpdateState = () => {
    updateSchoolCarriersData(schoolCarriersData);
  };

  onPrevious && onPrevious(handlePreviousStep);
  onNext && onNext(handleNextStep);
  onStepperSave && onStepperSave(handleUpdateState);

  return (
    <>
      {schoolCarriersData.map((schoolCarrierData, index) => (
        <ExpansionPanel key={schoolCarrierData.schoolCarrier.id}>
          <ExpansionPanelSummary
            expandIcon={<ExpandMoreIcon />}
            className={clsx(
              !validationResult.isArrayItemValid(
                TrackerName.SchoolCarrierData,
                index.toString()
              ) && classes.error
            )}
          >
            <Grid container spacing={4}>
              <Grid item md={2} lg={2}>
                <Typography variant="body2" color="textSecondary">
                  Vrsta vozila
                </Typography>
                <Typography variant="subtitle2">
                  {schoolCarrierData.schoolCarrier.vehicleTypeName}
                </Typography>
              </Grid>
              <Grid item md={3} lg={2}>
                <Typography variant="body2" color="textSecondary">
                  Registracijske oznake
                </Typography>
                <Typography variant="subtitle2">
                  {schoolCarrierData.schoolCarrier.registrationPlate}
                </Typography>
              </Grid>
              <Grid item md={3} lg={2}>
                <Typography variant="body2" color="textSecondary">
                  Ukupna dnevna kilometraža
                </Typography>
                <Typography variant="subtitle2">
                  {numberFormat(
                    schoolCarrierData.schoolCarrier.dailyMileageTotal,
                    "km",
                    4
                  )}
                </Typography>
              </Grid>
              <Grid item md={2} lg={1}>
                <Typography variant="body2" color="textSecondary">
                  Broj učenika
                </Typography>
                <Typography variant="subtitle2">
                  {schoolCarrierData.schoolCarrier.numberOfStudentsTotal}
                </Typography>
              </Grid>
              <Grid item md={2} lg={1}>
                <Typography variant="body2" color="textSecondary">
                  Broj dana
                </Typography>
                <Typography variant="subtitle2">
                  {schoolCarrierData.numberOfDays
                    ? schoolCarrierData.numberOfDays
                    : ""}
                </Typography>
              </Grid>
              <Grid item md={2} lg={2}>
                <Typography variant="body2" color="textSecondary">
                  Cijena
                </Typography>
                <Typography variant="subtitle2">
                  {schoolCarrierData.transportCostCorrected != null &&
                  !isNaN(schoolCarrierData.transportCostCorrected)
                    ? `${currencyFormat(
                        schoolCarrierData.transportCost || 0, currency
                      )}  (${currencyFormat(
                        schoolCarrierData.transportCostCorrected || 0, currency
                      )})`
                    : currencyFormat(schoolCarrierData.transportCost || 0, currency)}
                </Typography>
              </Grid>
              <Grid item md={2} lg={2} className={classes.largeColumn}>
                <Typography variant="body2" color="textSecondary">
                  Napomena
                </Typography>
                <Typography variant="subtitle2">
                  {schoolCarrierData.remark}
                </Typography>
              </Grid>
            </Grid>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails>
            <Grid container spacing={4}>
              <Grid item sm={12} md={6} lg={3}>
                <TrTextField
                  required
                  label="Broj dana"
                  name={nameof<typeof schoolCarrierData>("numberOfDays")}
                  value={
                    schoolCarrierData.numberOfDays != null
                      ? schoolCarrierData.numberOfDays
                      : ""
                  }
                  onChange={(e) =>
                    handleChange(e as ChangeEvent<HTMLInputElement>, index)
                  }
                  onBlur={(e) => handleCalculateCost(index)}
                  type="number"
                  fullWidth
                  error={
                    !validationResult.isArrayPropertyValid(
                      TrackerName.SchoolCarrierData,
                      index.toString(),
                      nameof<typeof schoolCarrierData>("numberOfDays")
                    )
                  }
                />
              </Grid>
              <Grid
                item
                sm={12}
                md={12}
                lg={12}
                className={classes.gridRowForSubTitle}
              >
                <Typography variant="h6">Korekcija iznosa</Typography>
              </Grid>
              <Grid
                item
                sm={12}
                md={12}
                lg={12}
                className={classes.gridRowForInfo}
              >
                <Card className={classes.cardInfo}>
                  <CardContent>
                    <div className={classes.cardInfoContent}>
                      <div className={classes.cardInfoIcon}>
                        <InfoOutlined />
                      </div>
                      <div className={classes.cardInfoText}>
                        {" "}
                        Upisujete samo u slučaju ako je ukupni iznos mjesečnog
                        troška prijevoznika manji od gore izračunatoga. U tom
                        slučaju, u polje upišite ukupni mjesečni iznos troškova!
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item sm={12} md={6} lg={3}>
                <TrTextField
                  label="Cijena"
                  name={nameof<typeof schoolCarrierData>(
                    "transportCostCorrected"
                  )}
                  value={
                    schoolCarrierData.transportCostCorrected != null
                      ? schoolCarrierData.transportCostCorrected
                      : ""
                  }
                  onChange={(e) =>
                    handleChange(e as ChangeEvent<HTMLInputElement>, index)
                  }
                  onBlur={(e) => handleCostCorrectedBlur(e, index)}
                  type="number"
                  tooltip={getTooltipForDecimalInput()}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{getCurrencyDisplayName(currency)}</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item sm={12} md={6} lg={3}>
                <TrTextField
                  disabled={
                    schoolCarrierData.transportCostCorrected == null ||
                    isNaN(schoolCarrierData.transportCostCorrected)
                  }
                  label="Napomena"
                  name={nameof<typeof schoolCarrierData>("remark")}
                  className={
                    schoolCarrierData.transportCostCorrected == null ||
                    isNaN(schoolCarrierData.transportCostCorrected)
                      ? classes.disabledTextField
                      : ""
                  }
                  value={
                    schoolCarrierData.remark != null
                      ? schoolCarrierData.remark
                      : ""
                  }
                  onChange={(e) =>
                    handleChange(e as ChangeEvent<HTMLInputElement>, index)
                  }
                  fullWidth
                />
              </Grid>
            </Grid>
          </ExpansionPanelDetails>
        </ExpansionPanel>
      ))}
      {buttons}
      <TrSnackbar
        variant={snackbarVariant}
        isOpened={isSnackbarOpened}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />
    </>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    error: {
      color: theme.palette.error.main,
    },
    largeColumn: {
      display: "flex",
      alignItems: "start",
      flexDirection: "column",
      flexGrow: 1.5,
      flexBasis: 0,
    },
    gridRowForSubTitle: {
      marginTop: theme.spacing(1),
    },
    gridRowForInfo: {
      marginTop: -theme.spacing(1),
    },
    cardInfo: {
      display: "inline-flex",
      flex: 1,
      background: theme.palette.primary.main.toString(),
      color: "white",
    },
    cardInfoContent: {
      display: "flex",
      alignItems: "center",
      marginBottom: "-8px",
    },
    cardInfoIcon: {
      display: "flex",
      marginRight: theme.spacing(1),
    },
    cardInfoText: {
      display: "flex",
    },
    disabledTextField: {
      opacity: 0.4,
    },
  })
);
